import { useMemo, useState } from "react";
import { portalApi, portalError, euros } from "@/lib/weddingApi";
import { CheckCircle2, AlertTriangle, Lock } from "lucide-react";

const labelOf = (catalog, id) => {
  for (const key of ["starters", "mains", "desserts"]) {
    const found = (catalog[key] || []).find((i) => i.id === id);
    if (found) return found.label;
  }
  return "Choix";
};

export default function SummaryTab({ wedding, catalog, patch, reload, setError }) {
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const tally = useMemo(() => {
    const present = (wedding.guests || []).filter((g) => g.attending);
    const build = (field, kind) => {
      const acc = {};
      present.forEach((g) => {
        if (kind === "child" && g.kind !== "child") return;
        if (kind === "adult" && g.kind === "child") return;
        if (g[field]) acc[g[field]] = (acc[g[field]] || 0) + 1;
      });
      return Object.entries(acc).map(([id, count]) => ({ id, count, label: labelOf(catalog, id) }));
    };
    return {
      adults: present.filter((g) => g.kind !== "child").length,
      children: present.filter((g) => g.kind === "child").length,
      absent: (wedding.guests || []).length - present.length,
      starters: build("starter_id", "adult"),
      mains: build("main_id", "adult"),
      desserts: build("dessert_id", "adult"),
      childMains: build("main_id", "child"),
      childDesserts: build("dessert_id", "child"),
      allergies: present.filter((g) => (g.allergies || "").trim() || (g.diet_note || "").trim()),
    };
  }, [wedding.guests, catalog]);

  const validate = async () => {
    if (!window.confirm("Confirmer définitivement vos invités et vos menus ? L'édition sera ensuite bloquée.")) return;
    setBusy(true);
    setErrors([]);
    try {
      await portalApi.post("/validate", { expected_version: wedding.version });
      setDone(true);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.errors) setErrors(detail.errors);
      else setError(portalError(e));
    } finally {
      setBusy(false);
    }
  };

  const Group = ({ title, items }) => (
    !!items.length && (
      <div className="mt-5">
        <p className="overline">{title}</p>
        <ul className="mt-2 space-y-1.5">
          {items.map((i) => (
            <li key={i.id} className="flex items-baseline justify-between gap-6 text-sm border-b border-dashed border-[#D9D3C5] pb-1.5">
              <span>{i.label}</span><span className="font-medium">{i.count}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  );

  return (
    <div data-testid="summary-tab" className="space-y-8">
      <section className="bg-white border border-[#D9D3C5] p-6 md:p-8">
        <h2 className="font-display text-2xl font-light">Vos effectifs</h2>
        <div className="mt-5 flex flex-wrap gap-x-10 gap-y-3">
          <div><p className="overline">Adultes</p><p data-testid="summary-adults" className="font-display text-3xl">{tally.adults}</p></div>
          <div><p className="overline">Enfants</p><p data-testid="summary-children" className="font-display text-3xl">{tally.children}</p></div>
          <div><p className="overline">Absents</p><p className="font-display text-3xl text-[#697585]">{tally.absent}</p></div>
        </div>
        {!!wedding.contract?.min_billable_adults && (
          <p className="mt-5 text-sm text-[#697585]">
            Minimum facturable au contrat : {wedding.contract.min_billable_adults} adultes
            {wedding.contract.min_billable_children ? ` et ${wedding.contract.min_billable_children} enfants` : ""}.
          </p>
        )}
        <p className="mt-2 text-sm text-[#697585]">Budget convenu : <strong>{euros(wedding.budget?.total_cents)}</strong></p>
      </section>

      <section className="bg-white border border-[#D9D3C5] p-6 md:p-8">
        <h2 className="font-display text-2xl font-light">Répartition des choix</h2>
        <Group title="Entrées" items={tally.starters} />
        <Group title="Plats" items={tally.mains} />
        <Group title="Desserts" items={tally.desserts} />
        <Group title="Plats enfants" items={tally.childMains} />
        <Group title="Desserts enfants" items={tally.childDesserts} />
      </section>

      {!!tally.allergies.length && (
        <section className="bg-white border border-[#D9D3C5] p-6 md:p-8">
          <h2 className="font-display text-2xl font-light">Allergies & régimes déclarés</h2>
          <ul className="mt-5 space-y-2">
            {tally.allergies.map((g) => (
              <li key={g.id} className="text-sm">
                <strong>{g.name}</strong>{g.table ? ` · table ${g.table}` : ""} — {g.allergies}{g.diet_note ? ` · ${g.diet_note}` : ""}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-[#697585]">
            Une allergie déclarée n'est jamais considérée comme résolue par un simple choix de plat :
            le restaurant valide chaque cas.
          </p>
        </section>
      )}

      {!!errors.length && (
        <div data-testid="validation-errors" className="border-l-2 border-[#B8956A] pl-4 space-y-2">
          <p className="flex items-center gap-2 text-sm text-[#B8956A]"><AlertTriangle size={16} /> Validation impossible :</p>
          <ul className="space-y-1">
            {errors.map((er, i) => <li key={i} className="text-sm text-[#203240]">· {er}</li>)}
          </ul>
        </div>
      )}

      {wedding.locked ? (
        <p data-testid="summary-locked" className="flex items-center gap-2 text-sm text-emerald-700">
          <Lock size={16} /> Vos informations sont confirmées et transmises au restaurant.
        </p>
      ) : (
        <button data-testid="summary-validate-btn" className="btn-primary inline-flex items-center gap-2" disabled={busy} onClick={validate}>
          <CheckCircle2 size={16} /> {busy ? "Validation…" : "Confirmer nos informations"}
        </button>
      )}

      {done && <p data-testid="summary-validated" className="text-sm text-emerald-700">Merci ! Vos choix ont été transmis.</p>}
    </div>
  );
}
